import React, { useState, useEffect } from 'react';
import appointmentService from '../../services/appointmentService';

const DailyAgenda = ({ onEditAppointment, refreshKey }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchAppointments();
  }, [selectedDate, refreshKey]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await appointmentService.getAppointmentsByDate(selectedDate);
      setAppointments(response.data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await appointmentService.updateAppointmentStatus(appointmentId, newStatus);
      fetchAppointments();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const handleDelete = async (appointment) => {
    if (!window.confirm(`Delete appointment for ${appointment.patient?.name}?`)) {
      return;
    }

    try {
      await appointmentService.deleteAppointment(appointment.id);
      fetchAppointments();
    } catch (error) {
      alert('Failed to delete appointment');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Missed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Cancelled':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Daily Agenda</h2>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      <div className="mb-4">
        <p className="text-lg text-gray-700">{formatDate(selectedDate)}</p>
        <p className="text-sm text-gray-500">
          {appointments.length} appointment{appointments.length !== 1 ? 's' : ''} scheduled
        </p>
      </div>

      {appointments.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📅</div>
          <p className="text-gray-500 text-lg">No appointments for this date</p>
        </div>
      ) : (
        <div className="space-y-4">
          {appointments.map((appointment) => (
            <div
              key={appointment.id}
              className={`border-l-4 p-4 rounded-r-lg ${getStatusColor(appointment.status)}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="text-2xl font-bold text-gray-700">
                      {appointment.appointment_time}
                    </span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(appointment.status)}`}>
                      {appointment.status}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900">
                    {appointment.patient?.name || 'Unknown Patient'}
                  </h3>
                  
                  <p className="text-sm text-gray-600">
                    📞 {appointment.patient?.phone || 'N/A'}
                  </p>
                  
                  {appointment.notes && (
                    <p className="text-sm text-gray-600 mt-2">
                      📝 {appointment.notes}
                    </p>
                  )}
                </div>

                <div className="flex flex-col space-y-2">
                  {appointment.status === 'Scheduled' && (
                    <>
                      <button
                        onClick={() => handleStatusChange(appointment.id, 'Completed')}
                        className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        ✓ Complete
                      </button>
                      <button
                        onClick={() => handleStatusChange(appointment.id, 'Missed')}
                        className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        ✗ Missed
                      </button>
                    </>
                  )}
                  
                  <button
                    onClick={() => onEditAppointment(appointment)}
                    className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  >
                    ✏️ Edit
                  </button>
                  
                  <button
                    onClick={() => handleDelete(appointment)}
                    className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DailyAgenda;